import React, { useEffect, useState } from "react";
import { Send, Loader2, Download } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const normName = (s) =>
  (s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[șş]/gi, "s")
    .replace(/[țţ]/gi, "t")
    .toLowerCase()
    .trim();


export default function SamedaySection({ order, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [services, setServices] = useState([]);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [counties, setCounties] = useState([]);
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({
    service: "7",
    pickup_point: "",
    county_id: "",
    city_id: "",
    weight: "5",
    cod_amount: String(order.total),
    name: order.customer.name || "",
    phone: order.customer.phone || "",
    address: order.customer.address || "",
  });

  const token = localStorage.getItem("mimaso_token");
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${order._id}/sameday-awb/pdf?token=${token}`;

  const openDialog = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const [cfg, cnt] = await Promise.all([
        api.get("/admin/sameday/config"),
        api.get("/admin/sameday/counties"),
      ]);
      setServices(cfg.data.services);
      setPickupPoints(cfg.data.pickup_points);
      setCounties(cnt.data);
      const def = cfg.data.pickup_points.find((p) => p.default) || cfg.data.pickup_points[0];
      const matchCounty = cnt.data.find((c) => normName(c.name) === normName(order.customer.county));
      setForm((f) => ({
        ...f,
        pickup_point: def ? String(def.id) : "",
        county_id: matchCounty ? String(matchCounty.id) : "",
      }));
      if (matchCounty) loadCities(matchCounty.id, order.customer.city);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Eroare conectare Sameday");
    } finally {
      setLoading(false);
    }
  };

  const loadCities = async (countyId, prefill = "") => {
    try {
      const res = await api.get("/admin/sameday/cities", { params: { county_id: countyId, name: prefill } });
      setCities(res.data);
      const match = res.data.find((c) => normName(c.name) === normName(prefill));
      if (match) setForm((f) => ({ ...f, city_id: String(match.id) }));
    } catch {
      setCities([]);
    }
  };

  const onCounty = (v) => {
    setForm((f) => ({ ...f, county_id: v, city_id: "" }));
    loadCities(v);
  };

  const generate = async () => {
    if (!form.name || !form.phone || !form.address) {
      toast.error("Completează nume, telefon și adresă destinatar");
      return;
    }
    if (!form.service || !form.pickup_point || !form.county_id || !form.city_id) {
      toast.error("Completează serviciu, punct ridicare, județ și oraș");
      return;
    }
    setGenerating(true);
    try {
      const res = await api.post(`/orders/${order._id}/sameday-awb`, {
        service: Number(form.service),
        pickup_point: Number(form.pickup_point),
        county_id: Number(form.county_id),
        city_id: Number(form.city_id),
        weight: Number(form.weight),
        cod_amount: Number(form.cod_amount),
        recipient_name: form.name,
        recipient_phone: form.phone,
        recipient_address: form.address,
      });
      toast.success(`AWB Sameday generat: ${res.data.sameday_awb}`);
      setOpen(false);
      onUpdated(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Eroare la generare AWB");
    } finally {
      setGenerating(false);
    }
  };

  if (order.sameday_awb) {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1.5 text-sm font-bold text-emerald-700" data-testid={`sameday-awb-${order._id}`}>
          Sameday AWB · {order.sameday_awb}
        </span>
        <a
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600"
          data-testid={`sameday-label-${order._id}`}
        >
          <Download className="h-4 w-4" /> Etichetă Sameday
        </a>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={openDialog}
        className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600"
        data-testid={`sameday-generate-${order._id}`}
      >
        <Send className="h-4 w-4" /> Generează AWB Sameday
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="sameday-dialog">
          <DialogHeader>
            <DialogTitle>Generează AWB Sameday</DialogTitle>
            <DialogDescription>Comandă #{order._id.slice(-6).toUpperCase()} · {order.customer.name}</DialogDescription>
          </DialogHeader>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
                <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Date destinatar</Label>
                <div className="mt-2 space-y-3">
                  <div>
                    <Label>Nume complet</Label>
                    <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5" data-testid="sameday-name" />
                  </div>
                  <div>
                    <Label>Telefon</Label>
                    <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="mt-1.5" data-testid="sameday-phone" />
                  </div>
                  <div>
                    <Label>Adresă</Label>
                    <Input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} className="mt-1.5" data-testid="sameday-address" />
                  </div>
                </div>
              </div>
              <div>
                <Label>Serviciu</Label>
                <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="sameday-service"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {services.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Punct de ridicare</Label>
                <Select value={form.pickup_point} onValueChange={(v) => setForm((f) => ({ ...f, pickup_point: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="sameday-pickup"><SelectValue placeholder="Alege" /></SelectTrigger>
                  <SelectContent>
                    {pickupPoints.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.alias} · {p.city}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Județ</Label>
                  <Select value={form.county_id} onValueChange={onCounty}>
                    <SelectTrigger className="mt-1.5" data-testid="sameday-county"><SelectValue placeholder="Județ" /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {counties.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Oraș/Localitate</Label>
                  <Select value={form.city_id} onValueChange={(v) => setForm((f) => ({ ...f, city_id: v }))} disabled={!form.county_id}>
                    <SelectTrigger className="mt-1.5" data-testid="sameday-city"><SelectValue placeholder="Oraș" /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {cities.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Greutate (kg)</Label>
                  <Input type="number" value={form.weight} onChange={(e) => setForm((f) => ({ ...f, weight: e.target.value }))} className="mt-1.5" data-testid="sameday-weight" />
                </div>
                <div>
                  <Label>Ramburs (lei)</Label>
                  <Input type="number" value={form.cod_amount} onChange={(e) => setForm((f) => ({ ...f, cod_amount: e.target.value }))} className="mt-1.5" data-testid="sameday-cod" />
                </div>
              </div>
              <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
                Atenție: AWB-ul se generează real în contul Sameday și poate genera costuri.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
            <Button onClick={generate} disabled={generating || loading} className="bg-orange-500 hover:bg-orange-600" data-testid="sameday-confirm">
              {generating ? "Se generează..." : "Generează AWB"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
